import WebViewerWrapper from 'pdftron/core/WebViewerWrapper';
import ToolsMixin from '../Tools';
import AnnotationMixin from 'pdftron/docManager/Annotation';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import {
  DocumentTypes,
  PDFTronTools,
  PolygonZoneCustomData,
  AnnotationCustomData,
  InputAnnotation,
  OPPOSITE_DOCUMENT,
  Quad,
} from 'types';
import Page from 'pdftron/core/Page';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import store from 'store/store';
import { inspection, app } from 'store';
import {
  getIframeDocument,
  setCursorStyle,
  getCursorPageNumber,
  CursorStyle,
  isPolygonCrop,
  resetCursors,
  getAllPageRectangularCropZones,
  updateCropAndPolygonOverlays,
  getAllPageCropAndPolygonZones,
} from './utils/CropToolUtils';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import quadsToDimensions from '../utils/quadsToDimensions';
import {
  pointInPolygon,
  getAllPagePolygonZones,
  cursorIsInsidePolygonZone,
  getPolygonPoints,
  validatePolygonCrop,
} from './utils/PolygonToolUtils';
import { trackEvent } from 'components/Tracker/TrackerProvider';

export interface PolygonZoneAnnotation {
  pageNumber: number;
  points: [number, number][];
  cropId?: string;
}

class PolygonZone {
  source: WebViewerWrapper | null = null;
  target: WebViewerWrapper | null = null;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get toolsMixin() {
    return new ToolsMixin(this.source, this.target);
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  createPolygonZoneAnnotation(instance: WebViewerInstance) {
    class PolygonZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.PolygonAnnotation) {
      constructor() {
        super();
        this.setCustomData(PolygonZoneCustomData.polygonAnnotation, 'true');
        this.ToolName = PDFTronTools.POLYGON;
        this.IsHoverable = true;
        this.disableRotationControl();

        this.FillColor = new instance.Core.Annotations.Color(255, 255, 255, 0.01); // Nearly transparent white
        this.StrokeColor = new instance.Core.Annotations.Color(205, 52, 52); // Red border
        this.StrokeThickness = 1;
      }
    }
    return PolygonZoneAnnotation;
  }

  createLayerOrSeparationShapeAnnotation(instance: WebViewerInstance) {
    class LayerOrSeparationShapeAnnotation extends GVAnnotationMixin(instance.Core.Annotations.PolygonAnnotation) {
      shouldDrawOverlay: boolean;
      constructor(shouldDrawOverlay = true) {
        super();
        this.setCustomData(PolygonZoneCustomData.polygonAnnotation, 'true');
        this.setCustomData(PolygonZoneCustomData.shapePolygon, 'true');
        this.ToolName = PDFTronTools.POLYGON;
        this.IsHoverable = true;
        this.NoMove = true;
        this.NoResize = true;
        this.disableRotationControl();
        this.shouldDrawOverlay = shouldDrawOverlay; // only draw overlay for the first polygon on the page
      }

      get page() {
        return new Page(instance, DocumentTypes.source);
      }

      get pageSize() {
        return this.page.getPageSize(this.PageNumber);
      }

      get annotationPoints() {
        return this.getPath();
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        const points = this.annotationPoints;
        this.setStyles(ctx, pageMatrix);

        ctx.save(); // Save canvas state before drawing

        // Step 1: draw the polygon
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.fillStyle = 'white'; // Fill color for the polygon
        ctx.fill();

        // NOTE: Overlay drawing is now handled by the coordinated system in CropToolUtils.ts
        // via updateCropAndPolygonOverlays(). This prevents overlay duplication.
        // The shouldDrawOverlay flag is deprecated but kept for backward compatibility.
        // Do NOT draw overlay here - it will be drawn by the coordinated system.

        // Step 4: Set the composite operation to 'destination-out' to cut out the polygon area
        ctx.globalCompositeOperation = 'destination-out';

        // Step 5: Draw the polygon again, but this time it will cut through the overlay
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.fillStyle = 'white';
        ctx.fill();

        ctx.restore(); // Restore canvas state after drawing
      }
    }
    return LayerOrSeparationShapeAnnotation;
  }

  createPolygonZoneAnnotationTools() {
    [this.source, this.target].forEach((wrapper: WebViewerWrapper | null) => {
      if (!wrapper) return;
      const { instance } = wrapper;
      class PolygonZoneCreateTool extends instance.Core.Tools.PolygonCreateTool {
        constructor(polygonZoneAnnotation: any) {
          super(instance.Core.documentViewer);
          instance.Core.Tools.GenericAnnotationCreateTool.call(
            this,
            instance.Core.documentViewer,
            polygonZoneAnnotation,
          );
          this.defaults.StrokeColor = new instance.Core.Annotations.Color(255, 0, 0);
        }

        switchOut(newTool: any) {
          super.switchOut(newTool);
          resetCursors();
        }
      }
      const toolObject = new PolygonZoneCreateTool(this.createPolygonZoneAnnotation(instance));
      const defaultMouseMoveEvent = toolObject.mouseMove.bind(toolObject);
      const originalMouseLeftDown = toolObject.mouseLeftDown?.bind(toolObject);

      toolObject.mouseLeftDown = (e: any) => {
        const currentPage = getCursorPageNumber(instance, toolObject, e);
        if (!currentPage) return;

        const state = store.getState();
        const activePolygonCreation = state.inspection.activePolygonCreation;
        const documentType = wrapper === this.source ? DocumentTypes.source : DocumentTypes.target;
        const polygonZones = getAllPagePolygonZones(instance, currentPage);

        // Check if polygon creation is active
        if (activePolygonCreation.isActive) {
          // If polygon creation is active on a different document, show warning
          if (activePolygonCreation.documentType !== documentType) {
            store.dispatch(
              app.actions.setSnackMessage({
                message: 'Please finish creating the polygon on the other document first.',
                type: 'info',
              }),
            );
            return;
          }

          // If polygon creation is active on the same document but different page, do nothing
          if (activePolygonCreation.currentPage !== currentPage) {
            return;
          }

          // Continue with normal polygon creation on the same document and page
          originalMouseLeftDown?.(e);
          return;
        }

        // Find if cursor is inside any polygon zone
        const hoveredPolygon = polygonZones.find((polygon) =>
          cursorIsInsidePolygonZone(e, polygon as Core.Annotations.PolygonAnnotation, wrapper, currentPage, true),
        );

        if (hoveredPolygon) {
          // Click is inside an existing polygon
          const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
          const polygonIsSelected = selectedAnnotation?.Id === hoveredPolygon.Id;

          if (!polygonIsSelected) {
            // Deselect all other annotations first, then select this polygon
            instance.Core.annotationManager.deselectAllAnnotations();
            instance.Core.annotationManager.selectAnnotation(hoveredPolygon);
            (toolObject as any).reset?.();
            return;
          }

          // Polygon is selected - allow editing if not locked
          if (!hoveredPolygon.Locked) {
            originalMouseLeftDown?.(e);
          }
        } else {
          // Click is in empty space - start new polygon creation
          store.dispatch(
            inspection.actions.startPolygonCreation({
              documentType,
              pageNumber: currentPage,
              currentPage,
            }),
          );
          originalMouseLeftDown?.(e);
        }
      };

      toolObject.mouseMove = (e: MouseEvent) => {
        // get current page
        const currentPage = getCursorPageNumber(instance, toolObject, e);
        if (!currentPage) return; // this only happens if cursor is in the space between pages

        // check for polygon zones
        const polygonZones = getAllPagePolygonZones(instance, currentPage);
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        const iframeDoc = getIframeDocument(wrapper, currentPage);
        if (!iframeDoc) return;

        // Check if we're actively creating a new annotation (either clicking or between clicks)
        const state = store.getState();
        const activePolygonCreation = state.inspection.activePolygonCreation;
        const isCreatingNewAnnotation = (toolObject as any).annotation || activePolygonCreation.isActive;

        // If no polygon zones on page, allow free creation
        if (polygonZones.length === 0) {
          const selectedTool = instance.Core.documentViewer.getToolMode();
          if (selectedTool) setCursorStyle(selectedTool.cursor, iframeDoc);
          defaultMouseMoveEvent(e);
          return;
        }

        // Find if cursor is inside any polygon zone
        const hoveredPolygon = polygonZones.find((polygon) =>
          cursorIsInsidePolygonZone(e, polygon as Core.Annotations.PolygonAnnotation, wrapper, currentPage, true),
        );
        const selectedPolygon = polygonZones.find((polygon) => polygon.Id === selectedAnnotation?.Id);

        // If actively creating a new polygon, allow drawing to continue even over existing polygons
        // Don't auto-select existing polygons during creation
        if (isCreatingNewAnnotation) {
          const selectedTool = instance.Core.documentViewer.getToolMode();
          if (selectedTool) setCursorStyle(selectedTool.cursor, iframeDoc);
          defaultMouseMoveEvent(e);
          return;
        }

        // If a polygon is selected and mouse is clicked: resizing or moving the polygon zone
        if (selectedPolygon && e.buttons === 1 && !selectedPolygon.Locked) {
          defaultMouseMoveEvent(e);
          return;
        }

        // Ignore other click events on polygon zones
        if (e.buttons === 1 && hoveredPolygon) {
          return;
        }

        // If hovering over a polygon zone
        if (hoveredPolygon) {
          // Check if a non-polygon annotation is selected
          const isNonCropSelected =
            selectedAnnotation &&
            selectedAnnotation.ToolName !== PDFTronTools.CROP &&
            selectedAnnotation.ToolName !== PDFTronTools.POLYGON;

          // Only auto-select on hover if no polygon is selected AND no other annotation type is selected
          // If another polygon is already selected, don't auto-select (allows accessing popup buttons)
          if (!selectedPolygon && !isNonCropSelected) {
            instance.Core.annotationManager.deselectAllAnnotations();
            instance.Core.annotationManager.selectAnnotation(hoveredPolygon);
          }

          // Set cursor based on whether the polygon is locked and if it's selected
          if (hoveredPolygon.Locked) {
            setCursorStyle(CursorStyle.POINTER, iframeDoc);
          } else if (selectedPolygon && selectedPolygon.Id === hoveredPolygon.Id) {
            // When hovering over the selected polygon, use default cursor (allows PDFTron to handle resize handles)
            setCursorStyle(CursorStyle.DEFAULT, iframeDoc);
          } else {
            // When hovering over unselected polygon, show pointer to indicate it's clickable
            setCursorStyle(CursorStyle.POINTER, iframeDoc);
          }

          // Only call defaultMouseMoveEvent when this polygon is selected (for editing behavior)
          if (selectedPolygon && selectedPolygon.Id === hoveredPolygon.Id) {
            defaultMouseMoveEvent(e);
          }
        } else {
          // Not hovering over any polygon zone
          // If a polygon was selected, deselect it
          if (selectedPolygon) {
            instance.Core.annotationManager.deselectAnnotation(selectedPolygon);
          }
          // Allow creating new polygon zones in empty areas
          const selectedTool = instance.Core.documentViewer.getToolMode();
          if (selectedTool) setCursorStyle(selectedTool.cursor, iframeDoc);
          defaultMouseMoveEvent(e);
        }
      };

      wrapper.registerTool({
        toolName: PDFTronTools.POLYGON,
        toolObject,
        buttonImage: '',
      });
    });
  }

  reloadPolygonZones(sourcePolygonZones: InputAnnotation[], targetPolygonZones: InputAnnotation[]) {
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;

    for (const webviewer of [this.source, this.target]) {
      if (!webviewer) continue;
      const annotManager = webviewer.annotationManager;
      const markups = webviewer === this.source ? sourcePolygonZones : targetPolygonZones;
      const documentType = webviewer === this.source ? DocumentTypes.source : DocumentTypes.target;
      const pagesWithPolygons = new Set<number>();

      // Add all polygon annotations first
      for (const markup of markups) {
        const existingAnnotation = annotManager.getAnnotationById(markup.annotationId);
        if (!existingAnnotation) {
          const annotation = new (docManager.createPolygonZoneAnnotation(webviewer.instance))();
          annotation.Id = markup.annotationId;
          annotation.PageNumber = markup.page;
          if (markup.cropZone && isPolygonCrop(markup.cropZone)) {
            const { points } = markup.cropZone;

            points.forEach((point) => {
              annotation.addPathPoint(point[0], point[1]);
            });
          }
          annotation.ReadOnly = false;
          annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');

          annotManager.addAnnotation(annotation);
          annotManager.redrawAnnotation(annotation);
          pagesWithPolygons.add(markup.page);
        }
      }

      // Refresh overlay once per page after all polygons are added
      for (const pageNumber of pagesWithPolygons) {
        this.refreshPagePolygonOverlay(documentType, pageNumber, false);
      }

      if (pagesWithPolygons.size > 0) {
        webviewer.instance.Core.documentViewer.refreshAll();
        webviewer.instance.Core.documentViewer.updateView();
      }
    }
    store.dispatch(inspection.actions.setCropZoneId(sourcePolygonZones.length + targetPolygonZones.length + 1));
  }

  refreshPagePolygonOverlay(documentType: DocumentTypes, pageNumber: number, refresh = true) {
    const documentWrapper = PDFManagerFactory.getViewer(documentType);
    if (!documentWrapper) return;

    // Use the unified refresh function that updates both crops and polygons
    updateCropAndPolygonOverlays(documentWrapper.instance, pageNumber, refresh);
  }

  addPolygonOverlay(documentType: DocumentTypes, polygonAnnotation: Core.Annotations.Annotation, refresh = true) {
    // Instead of drawing overlay on individual annotation, refresh the entire page overlay
    this.refreshPagePolygonOverlay(documentType, polygonAnnotation.PageNumber, refresh);
  }

  removePolygonOverlay(documentType: DocumentTypes, polygonAnnotation: Core.Annotations.Annotation, refresh = true) {
    const documentWrapper = PDFManagerFactory.getViewer(documentType);

    if (!documentWrapper) return;

    // Restore default annotation drawing by removing the custom draw override
    // This prevents polygon lines from being removed during resizing
    delete (polygonAnnotation as any).draw;

    if (refresh) {
      documentWrapper.instance.Core.documentViewer.refreshAll();
      documentWrapper.instance.Core.documentViewer.updateView();
    }
  }

  deleteAllPolygonAnnotations(documentType: DocumentTypes): void {
    // Delete all polygon annotations from the annotation manager
    const inputAnnotations = PDFAnnotationManager.getInputAnnotations(documentType);
    const annotationManager = PDFAnnotationManager.getInstance(documentType);
    if (!inputAnnotations || !annotationManager) {
      return;
    }

    const annotationsToDelete: Core.Annotations.Annotation[] = inputAnnotations
      .filter((inputAnnot) => {
        const annotation = annotationManager.getAnnotationById(inputAnnot.annotationId);
        return annotation.getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true';
      })
      .map((inputAnnotation) => annotationManager.getAnnotationById(inputAnnotation.annotationId));

    annotationManager.deleteAnnotations(annotationsToDelete, {
      imported: true,
      force: true,
    });

    PDFManagerFactory.getPDFDocManager()?.updateInputAnnotations();
  }

  drawPolygons(documentType: DocumentTypes, pagePoints: any) {
    if (!pagePoints) return;
    const documentWrapper = PDFManagerFactory.getViewer(documentType);
    if (!documentWrapper) return;
    const instanceSource = documentWrapper.instance;
    const manager = PDFManagerFactory.getPDFDocManager();
    if (!manager || !instanceSource) return;

    const pagesWithShapes = new Set<number>();

    for (let i = 0; i < pagePoints.length; i++) {
      const polygonShapes = pagePoints[i];
      const pageNumber = i + 1;
      if (!polygonShapes?.length) {
        continue;
      }
      // Note: shouldDrawOverlay parameter is deprecated but kept for backward compatibility
      // Overlays are now managed by the coordinated system in updateCropAndPolygonOverlays()
      const isFirstPolygon = false; // Always false - coordinated system handles overlays

      for (let j = 0; j < polygonShapes.length; j++) {
        const points = polygonShapes[j];

        const annotation = new (manager.createLayerOrSeparationShapeAnnotation(instanceSource))(isFirstPolygon);

        for (let k = 0; k < points.length; k++) {
          annotation.addPathPoint(points[k][0], points[k][1]);
        }
        annotation.PageNumber = pageNumber;

        // Mark as programmatic draw to avoid autosave/validation on ADD
        annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');

        documentWrapper.annotationManager.addAnnotation(annotation);
        pagesWithShapes.add(annotation.PageNumber);
      }
    }

    // Use coordinated overlay system for all pages with shapes
    pagesWithShapes.forEach((pageNumber) => {
      updateCropAndPolygonOverlays(instanceSource, pageNumber, false);
    });

    instanceSource.Core.documentViewer.refreshAll();
    instanceSource.Core.documentViewer.updateView();
  }

  deleteAllPolygonZones(documentType: DocumentTypes) {
    store.dispatch(
      inspection.actions.setPolygons({
        documentType,
        points: '',
        type: '',
        key: '',
        layerOrSeparationName: '',
      }),
    );
  }

  getDeletePolygonPopup(instance: WebViewerInstance, documentType: DocumentTypes) {
    return {
      type: 'actionButton',
      img: '/icons/zoneDelete.svg',
      title: 'Delete',
      onClick: () => {
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        instance.Core.annotationManager.deleteAnnotation(selectedAnnotation);

        // get all polygon annotations
        const allAnnotations = instance.Core.annotationManager.getAnnotationsList();
        const polygons = allAnnotations.filter((annotation) => {
          return annotation.getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true';
        });
        if (polygons.length === 0) {
          this.deleteAllPolygonZones(documentType);
        }
      },
    };
  }

  annotationsExistOutsideNewPolygon(
    existingAnnotations: InputAnnotation[] | { quads: any }[],
    newPolygon: Core.Annotations.Annotation,
    documentType: DocumentTypes,
  ): boolean {
    const newPolygonPoints = (newPolygon as Core.Annotations.PolygonAnnotation).getPath();

    if (!newPolygonPoints || newPolygonPoints.length === 0) {
      return false;
    }

    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (!instance) return false;

    // Boundary tolerance to allow annotations on or very close to crop zone boundaries
    // Using a larger tolerance (5 points) to handle snap-to-content edge cases
    const BOUNDARY_TOLERANCE = 5;

    // Get all existing crop and polygon zones on this page (excluding the new one)
    const existingCropZones = getAllPageRectangularCropZones(instance, newPolygon.PageNumber);
    const existingPolygonZones = getAllPagePolygonZones(instance, newPolygon.PageNumber);

    // Check each annotation to see if it's covered by the new polygon OR any existing crop/polygon
    return existingAnnotations.some((existingAnnot) => {
      const { quads } = existingAnnot;
      const rect = quadsToDimensions(quads as Quad[]);

      if (!rect) return false;

      // Calculate the bounding box corners for this annotation
      const annotBounds = {
        x1: rect.X,
        y1: rect.Y,
        x2: rect.X + rect.Width,
        y2: rect.Y + rect.Height,
      };

      const corners = [
        { x: annotBounds.x1, y: annotBounds.y1 }, // top-left
        { x: annotBounds.x2, y: annotBounds.y1 }, // top-right
        { x: annotBounds.x2, y: annotBounds.y2 }, // bottom-right
        { x: annotBounds.x1, y: annotBounds.y2 }, // bottom-left
      ];

      // Shrink corners inward to apply tolerance for the new polygon check
      const tolerantCorners = corners.map((corner, idx) => {
        const isLeft = idx === 0 || idx === 3;
        const isTop = idx === 0 || idx === 1;
        return {
          x: corner.x + (isLeft ? BOUNDARY_TOLERANCE : -BOUNDARY_TOLERANCE),
          y: corner.y + (isTop ? BOUNDARY_TOLERANCE : -BOUNDARY_TOLERANCE),
        };
      });

      // Check if annotation is inside the new polygon (with tolerance)
      const isInsideNewPolygon = tolerantCorners.every((corner) => pointInPolygon(corner, newPolygonPoints));
      if (isInsideNewPolygon) return false; // Annotation is covered by new polygon

      // Check if annotation is inside any existing crop zone (with tolerance)
      const isInsideExistingCrop = existingCropZones.some((cropZone) => {
        const cropRect = cropZone.getRect();
        return (
          annotBounds.x1 >= cropRect.x1 - BOUNDARY_TOLERANCE &&
          annotBounds.x2 <= cropRect.x2 + BOUNDARY_TOLERANCE &&
          annotBounds.y1 >= cropRect.y1 - BOUNDARY_TOLERANCE &&
          annotBounds.y2 <= cropRect.y2 + BOUNDARY_TOLERANCE
        );
      });

      if (isInsideExistingCrop) return false; // Annotation is covered by existing crop

      // Check if annotation is inside any existing polygon zone (with tolerance)
      const isInsideExistingPolygon = existingPolygonZones.some((polygonZone) => {
        const polygonPoints = (polygonZone as Core.Annotations.PolygonAnnotation).getPath();
        if (!polygonPoints || polygonPoints.length === 0) return false;

        return tolerantCorners.every((corner) => pointInPolygon(corner, polygonPoints));
      });

      if (isInsideExistingPolygon) return false; // Annotation is covered by existing polygon

      // If annotation is not covered by new polygon or any existing crop/polygon, it's outside
      return true;
    });
  }

  polygonZoneAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    const thisDocInputAnnotations =
      documentType === DocumentTypes.source
        ? PDFAnnotationManager.getInputAnnotations(DocumentTypes.source) || []
        : PDFAnnotationManager.getInputAnnotations(DocumentTypes.target) || [];

    if (action === AnnotationAction.ADD) {
      // Adding this as a failsafe - this shouldn't happen but we've had an issue popup in sentry where polygon crops with no points & more than one crop per page are being passed in the input json file
      if (!validatePolygonCrop(annotation)) {
        PDFAnnotationManager.deleteAnnotationById(documentType, annotation.Id);
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'Invalid polygon crop. At least 3 points required.',
            type: 'error',
          }),
        );
        return;
      }

      if (annotation.getCustomData(AnnotationCustomData.drawMarkups) !== 'true') {
        const nonPolygonAnnotationsOnPage = thisDocInputAnnotations?.filter(
          (annot) => annot.usedTool !== PDFTronTools.POLYGON && annot.page === annotation.PageNumber,
        );

        // Show error and delete annotation if annotations exist outside the new polygon zone
        if (
          nonPolygonAnnotationsOnPage?.length &&
          this.annotationsExistOutsideNewPolygon(nonPolygonAnnotationsOnPage, annotation, documentType)
        ) {
          PDFAnnotationManager.deleteAnnotationById(documentType, annotation.Id);
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'Please draw the polygon zone around any existing markups on the page.',
              type: 'error',
            }),
          );
          return;
        }

        // Exit polygon creation when a new polygon is successfully added
        store.dispatch(inspection.actions.exitPolygonCreation());

        trackEvent({
          name: 'polygon-crop',
          data: {
            documentType,
            pageNumber: annotation.PageNumber,
            cropCoordinates: getPolygonPoints(annotation as Core.Annotations.PolygonAnnotation),
          },
        });

        // Show success for the first polygon zone added to inspection
        const bothDocsInputAnnots = thisDocInputAnnotations.concat(
          PDFAnnotationManager.getInputAnnotations(OPPOSITE_DOCUMENT[documentType]) || [],
        );
        const allPolygonZones = bothDocsInputAnnots?.filter(
          (annot) => annot.usedTool === PDFTronTools.POLYGON && annot.cropZone,
        );
        if (allPolygonZones?.length === 1) {
          store.dispatch(
            app.actions.setSnackMessage({
              message: `Your polygon zone was created! You're ready to inspect.`,
              type: 'success',
            }),
          );
        }
      }
    }

    // For DELETE actions, check if there are remaining zones (crops or polygons) on the page
    if (action === AnnotationAction.DELETE) {
      const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
      const allPageZones = getAllPageCropAndPolygonZones(instance, annotation.PageNumber);
      // Filter out the annotation being deleted to get truly remaining zones
      const remainingZones = allPageZones.filter((zone) => zone.Id !== annotation.Id);

      if (remainingZones.length > 0) {
        // Refresh overlay for remaining zones on this page
        // The overlay will automatically be drawn by the next zone (first by ID) due to dynamic determination
        this.refreshPagePolygonOverlay(documentType, annotation.PageNumber);
      } else {
        // No zones left on page - clear any remaining overlays
        const documentWrapper = PDFManagerFactory.getViewer(documentType);
        if (documentWrapper) {
          documentWrapper.instance.Core.documentViewer.refreshAll();
          documentWrapper.instance.Core.documentViewer.updateView();
        }
      }
    } else {
      // For ADD and MODIFY actions, refresh the page overlay
      this.addPolygonOverlay(documentType, annotation);
    }

    // Check if this is a reload (ADD action with drawMarkups = 'true') vs user-initiated change
    // Only autosave for user-initiated changes, not the redraw
    const shouldAutoSave = !(
      action === AnnotationAction.ADD && annotation.getCustomData(AnnotationCustomData.drawMarkups) === 'true'
    );

    // Only trigger autosave/state sync for user-initiated changes, not the programmatic redraw
    if (shouldAutoSave) {
      this.annotationMixin.updateInputAnnotations(true);
    }
  }

  polygonZoneAnnotationSelected(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, _action: string) {
    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (instance) {
      const popupButtons = [this.getDeletePolygonPopup(instance, documentType)];
      instance.UI.annotationPopup.update(popupButtons);
    }
  }
}

export default PolygonZone;
